import React, { useCallback, useEffect, useMemo, useState } from "react";
import Popup from "reactjs-popup";
import DataTable from "../../../Elements/DataTable";
import Pagination, { Pagination2 } from "../../../Elements/Pagination";
import EmptyDataAlert from "../../../Elements/EmptyDataAlert";
import * as XLSX from "xlsx";
import { fetchLoanList } from "../../../../../../API/Bank/LoanSearch";
import cookies from "../../../../../../CommonFunction/cookies";
import { usePagination, useTable } from "react-table";
import { DashboardLoading } from "../../../Elements/ServeLoading";
import { FaDownload, FaSpinner } from "react-icons/fa";

const LoanAccounts = ({ isOpen, onClose, bankID, branchID, role }) => {
  const jwtToken = cookies.get("jwt");
  const [loanData, setLoanData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showLoanDetails, setShowLoanDetails] = useState(false);
  const [isExport, setIsExport] = useState(true);
  const [isExportLoading, setExportLoading] = useState();
  const [error, setError] = useState(false);
  const [pagination, setPagination] = useState({
    totalDocs: 0,
    totalPages: 1,
    page: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const columns = useMemo(
    () => [
      {
        Header: "Linked Loan",
        accessor: "identity_number",
        Cell: ({ row, value }) => (
          <div className="my-1 px-2">
            <p>{value}</p>
          </div>
        ),
      },
      {
        Header: "Borrower Name",
        accessor: "positions",
        Cell: ({ row }) => {
          const borrowerPositions = row?.original?.positions?.filter(
            (position) => position.role === "borrower"
          );

          return (
            <div className="text-left border-bgclr2 p-1">
              {borrowerPositions.length > 0 ? (
                borrowerPositions.map((position, index) => {
                  const entity = position?.user || position?.organization;
                  const name = entity?.first_name
                    ? `${entity?.first_name} ${entity?.last_name}`
                    : entity?.name;
                  return (
                    <div key={index} style={{ height: "auto" }}>
                      <div className="p-[1px] text-center">
                        {name
                          ? name.charAt(0).toUpperCase() + name.slice(1)
                          : "Null"}{" "}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-gray-500">No Borrower</p>
              )}
            </div>
          );
        },
      },
      {
        Header: "UCI",
        accessor: "system_id",
        Cell: ({ row }) => {
          const borrowerPositions = row?.original?.positions?.filter(
            (position) => position.role === "borrower"
          );

          return (
            <div className="text-center">
              {borrowerPositions.length > 0 ? (
                borrowerPositions.map((position, index) => (
                  <div key={index} className="border-bgclr2 my-1 px-2">
                    {position.user
                      ? position?.user?.system_id
                      : position?.organization?.system_id || "Null"}
                  </div>
                ))
              ) : (
                <p className="text-gray-500">No UCI</p>
              )}
            </div>
          );
        },
      },
      {
        Header: "Loan Amount",
        accessor: "amount",
        Cell: ({ value }) => (
          <div className="text-center border-bgclr2">
            <div className="px-2 border-bgclr2 my-3">{value}</div>
          </div>
        ),
      },
      {
        Header: "Branch",
        accessor: "branch",
        Cell: ({ value }) => (
          <div className="text-center border-bgclr2">
            <div className="px-2 border-bgclr2 my-3">{value?.name}</div>
          </div>
        ),
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => (
          <div className="text-center border-bgclr2">
            <div className="px-2 border-bgclr2 my-3">{value}</div>
          </div>
        ),
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow } =
    useTable(
      {
        columns,
        data: loanData,
        initialState: { pageIndex: 0 },
        manualPagination: true,
      },
      usePagination
    );

  const fetchData = useCallback(
    async (page = 1, query = searchQuery) => {
      setLoading(true);
      try {
        const data = await fetchLoanList(
          !isExport,
          page,
          query,
          jwtToken,
          bankID,
          branchID,
          role
        );
        if (data?.docs) {
          setLoanData(data.docs);
          setPagination({
            totalDocs: data.totalDocs,
            totalPages: data.totalPages,
            page: data.page,
            limit: data.limit,
            hasNextPage: data.hasNextPage,
            hasPrevPage: data.hasPrevPage,
          });
          setError(false);
        } else {
          setLoanData([]);
          setError(true);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [jwtToken]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const exportToExcel = async () => {
    setExportLoading(true);
    const loansDetails = await fetchLoanList(
      isExport,
      "",
      "",
      jwtToken,
      bankID,
      branchID,
      role
    );
    setExportLoading(false);
    const ws = XLSX.utils.json_to_sheet(loansDetails?.docs);

    // Set column widths
    const columnWidths = [
      { wch: 20 },
      { wch: 25 },
      { wch: 30 },
      { wch: 15 },
      { wch: 25 },
      { wch: 10 },
      { wch: 20 },
      { wch: 30 },
      { wch: 15 },
      { wch: 25 },
      { wch: 10 },
      { wch: 20 },
    ];
    ws["!cols"] = columnWidths;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "LoanData");
    XLSX.writeFile(wb, "Loan_report.xlsx");
  };

  const handleSearch = async () => {
    setLoading(true);
    fetchData(1, searchQuery);
  };

  const nextPage = useCallback(() => {
    if (pagination.hasNextPage) fetchData(pagination.page + 1, searchQuery);
  }, [fetchData, searchQuery, pagination]);

  const previousPage = useCallback(() => {
    if (pagination.hasPrevPage) fetchData(pagination.page - 1, searchQuery);
  }, [fetchData, searchQuery, pagination]);

  return (
    <>
      <Popup
        open={isOpen}
        modal
        closeOnDocumentClick={false}
        className="custom-popup"
      >
        <div className="popup-style popup-content">
          <div className="bg-bgclr8 md:w-[950px] w-[400px]  mx-auto h-[520px] rounded-[14px]">
            <div className="flex justify-between font-avenir text-bgclr px-10 pt-5">
              <h1 className="text-[20px] font-[600] ">Total Linked Loans</h1>
              <div className="flex gap-4">
                <button
                  type="submit"
                  onClick={() => {
                    exportToExcel();
                  }}
                  disabled={loading || isExportLoading}
                  className="flex gap-2 items-center py-1 px-7  bg-bgclr rounded-md text-txtclr"
                >
                  {isExportLoading ? (
                    <FaSpinner size={18} className="animate-spin text-txtclr" />
                  ) : (
                    <FaDownload size={18} />
                  )}{" "}
                  Download Report
                </button>
                <button
                  type="submit"
                  onClick={() => {
                    onClose();
                  }}
                  // disabled={loading || isExportLoading}
                  className="font-bold py-1 px-3 rounded-md bg-red-700 text-txtclr"
                >
                  X
                </button>
              </div>
            </div>
            <div>
              {!showLoanDetails ? (
                <div>
                  <div className="searchBar-style-dashboard">
                    <input
                      type="search"
                      placeholder="Search by loan number"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="searchBar-input-dashboard"
                      onInput={(e) => {
                        e.target.value = e.target.value.replace(/[^0-9]/g, "");
                      }}
                    />
                    <button
                      onClick={() => handleSearch()}
                      className="searchBar-btn-dashboard"
                    >
                      Search
                    </button>
                  </div>
                  {loading ? (
                    <div className="flex justify-center h-[360px] items-center">
                      <DashboardLoading />
                    </div>
                  ) : (
                    <>
                      {loanData.length === 0 ? (
                        <div className="h-screen">
                          <EmptyDataAlert
                            title={
                              error
                                ? "No Loans Found"
                                : "Search Loan by Loan number"
                            }
                          />
                        </div>
                      ) : (
                        <div className="mt-2">
                          <div>
                            <div className="pt-3 w-[900px] mx-auto overflow-x-auto h-[335px] overflow-y-auto ">
                              <DataTable
                                getTableProps={getTableProps}
                                headerGroups={headerGroups}
                                getTableBodyProps={getTableBodyProps}
                                page={page}
                                prepareRow={prepareRow}
                              />
                            </div>
                            <Pagination2
                              previousPage={previousPage}
                              nextPage={nextPage}
                              canPreviousPage={pagination.hasPrevPage}
                              canNextPage={pagination.hasNextPage}
                              pagination={{
                                page: pagination.page,
                                totalPages: pagination.totalPages,
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div></div>
              )}
            </div>
          </div>
        </div>
      </Popup>
    </>
  );
};

export default LoanAccounts;
